import API from '@API/index';
import { Check, Error, Settings } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, Fab, Tooltip, Typography } from '@mui/material';
import Utils from '@utils/index';
import { first, isEmpty, isString, isUndefined } from 'lodash';
import pLimit from 'p-limit';
import React from 'react';
import { v4 } from 'uuid';

function getCameraNumber(stream: VideoStream): string {
  let deviceId = '1';

  if (stream?.rtspUrl) {
    const regex = /[?&]camera=([^&]*)/;
    const match = stream.rtspUrl.match(regex);
    if (match && match[1]) {
      deviceId = `${match[1]}`;
    }
  }

  return deviceId;
}

function getEdgeDeviceToSendMessage(streams: VideoStream[]): string {
  let EDGE_DEVICE = process.env.REACT_APP_STACK === 'prod' ? 'a8_a1_59_da_31_b1' : 'a8_a1_59_9e_d4_54';

  if (process.env.REACT_APP_STACK === "prod") {
    const target = streams.find(stream =>
      !isUndefined(stream?.iotThingName) &&
      !isEmpty(stream?.iotThingName) &&
      isString(stream?.iotThingName) &&
      stream?.iotThingName !== "DETACHED"
    )

    if (target && target?.iotThingName) {
      EDGE_DEVICE = target.iotThingName;
      console.log('Edge device found: ', EDGE_DEVICE)
    }
  }

  return EDGE_DEVICE;
}

function performConfigureGeofenceRequest(streams: VideoStream[], onSuccess: (streams: VideoStream[]) => void, onError: (streams: VideoStream[]) => void, onTimeout: (streams: VideoStream[]) => void): Promise<"success" | "error" | "timeout"> {
  return new Promise((resolve) => {
    let resolved: boolean = false;

    let EDGE_DEVICE = getEdgeDeviceToSendMessage(streams);

    const uuid: string = v4();
    const sub = API.PubSub.subscribeToTopic(`edge/${EDGE_DEVICE}/http/responses/${uuid}`, (message: any) => {
      console.log("Incoming message: ", message);
      sub?.unsubscribe();

      if (resolved) return;

      if (message && message?.status && message.status === 200) {
        onSuccess(streams);
        resolved = true;
        resolve("success");
      } else {
        resolved = true;
        onError(streams);
        resolve("error")
      }
    });

    const message = {
      "device": {
        "manufacturer": first(streams)?.manufacturer || "",
        "type": first(streams)?.type || "",
        "url": Utils.RegEx.findIPAddress(first(streams)?.rtspUrl || "")
      },
      "action": "geofences/map-to-axis-camera",
      "parameters": {
        "deviceStreams": streams.map(stream => (
          {
            "deviceId": Number(stream.externalId?.split('-')[1]) || "",
            "kvsName": stream.kvsName,
            "cameraModel": stream.cameraModel || ""
          }
        ))
      }
    };

    setTimeout(() => {
      API.PubSub.publishOnTopic(`edge/${EDGE_DEVICE}/http/requests/${uuid}`, message)
    }, 1000)

    setTimeout(() => {
      if (resolved === false) {
        sub?.unsubscribe();
        console.log('timeout')
        resolved = true;
        onTimeout(streams);
        resolve("timeout");
      }
    }, 30000)
  })
}

type Request = {
  kvsName: string;
  name?: string;
}

type CustomSiteStreamsConfigurationProps = {
  streams: VideoStream[];
  staticMode?: boolean;
  onClose?: () => void;
}

const CustomStreamsConfiguration: React.FC<CustomSiteStreamsConfigurationProps> = (props) => {
  const { streams, staticMode, onClose } = props;

  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [startedConfiguration, setStartedConfiguration] = React.useState<boolean>(false);

  const [pending, setPending] = React.useState<Request[]>([]);
  const [skipped, setSkipped] = React.useState<Request[]>([]);
  const [success, setSuccess] = React.useState<Request[]>([]);
  const [failed, setFailed] = React.useState<Request[]>([]);

  const subsRef = React.useRef<any[]>([]);

  const openDialog = React.useCallback(() => {
    setDialogOpen(true)
  }, [])

  const closeDialog = React.useCallback(() => {
    setDialogOpen(false)
    if (onClose) onClose();
  }, [onClose])

  const startAutopilotRoutine = React.useCallback(() => {
    setStartedConfiguration(true);

    const skipped: Request[] = [];
    const pending: Request[] = [];

    streams.forEach((stream) => {
      if (stream.cameraModel === 'Q6010-E') {
        pending.push({ name: `${stream.defaultName}`, kvsName: stream.kvsName });
      } else {
        skipped.push({ name: `${stream.defaultName}`, kvsName: stream.kvsName });
      }
    })

    setSkipped(skipped);
    setPending(pending);

    const streamsToConfigure = streams.filter(el => pending.find(e => e.kvsName === el.kvsName));

    const devices = new Set<string>();
    streamsToConfigure.forEach((e) => devices.add(Utils.RegEx.findIPAddress(e.rtspUrl)));
    const map = new Map<string, VideoStream[]>();
    Array.from(devices).forEach((name) => {
      map.set(name, streamsToConfigure.filter(stream => Utils.RegEx.findIPAddress(stream.rtspUrl) === name));
    })

    let currentIndex = 0;

    console.log(map);

    map.forEach((streams) => {
      const EDGE_DEVICE = getEdgeDeviceToSendMessage(streams);
      const uuid: string = v4();
      const sub = API.PubSub.subscribeToTopic(`edge/${EDGE_DEVICE}/http/responses/${uuid}`, (message: any) => {
        console.log("Incoming message: ", message);
        subsRef.current.push(sub);
        sub.unsubscribe();
        if (message) {
          if (message.status) {
            if (message.status === 200) {
              setSuccess((currentValue) => [...currentValue, ...streams.map(stream => ({ kvsName: stream.kvsName, name: `${stream.defaultName} (${stream.externalId})` }))])
              setPending((p) => p.filter(el => streams.find(stream => stream.kvsName === el.kvsName) === undefined))
            } else {
              setFailed((currentValue) => [...currentValue, ...streams.map(stream => ({ kvsName: stream.kvsName, name: `${stream.defaultName} (${stream.externalId})` }))])
              setPending((p) => p.filter(el => streams.find(stream => stream.kvsName === el.kvsName) === undefined))
            }
          }
        }
      });

      const message = {
        "device": {
          "manufacturer": first(streams)?.manufacturer || "",
          "type": first(streams)?.type || "",
          "url": Utils.RegEx.findIPAddress(first(streams)?.rtspUrl || "")
        },
        "action": "autopilot/configure",
        "parameters": {
          "cameraModel": "Q6010-E",
          "deviceStreams": streams.map(stream => ({
            "deviceId": +getCameraNumber(stream),
            "kvsName": stream.kvsName
          }))
        }
      };

      console.log('Sending configuration to topic: ', `edge/${EDGE_DEVICE}/http/requests/${uuid}`);
      console.log('Message: ', message)
      setTimeout(() => {
        API.PubSub.publishOnTopic(`edge/${EDGE_DEVICE}/http/requests/${uuid}`, message)
      }, 1000 * currentIndex);
      currentIndex += 1;
    })
  }, [streams])

  const startAutofocusRoutine = React.useCallback(() => {
    setStartedConfiguration(true);

    const skipped: Request[] = [];
    const pending: Request[] = [];

    const supportedModels = [
      "P1455-LE",
      "P3267-LVE",
      "P4707-PLVE",
      "P3735-PLE",
      "P1467-LE",
      "P4705-PLVE",
      "P1465-LE",
      "Q6010-E",
      "P3265-LVE",
      "P3247-LVE",
      "P3245-LVE",
      "P1468-LE",
      "P1377-LE",
      "P3727-PLE",
      "Q3536-LVE",
      "P3719-PLE",
      "Q1805-LE",
      "Q1647",
      "P3717-PLE"
    ]

    streams.forEach((stream) => {
      if (supportedModels.includes(stream.cameraModel || "")) {
        pending.push({ name: `${stream.defaultName}`, kvsName: stream.kvsName });
      } else {
        skipped.push({ name: `${stream.defaultName}`, kvsName: stream.kvsName });
      }
    })

    setSkipped(skipped);
    setPending(pending);

    const streamsToConfigure = streams.filter(el => pending.find(e => e.kvsName === el.kvsName));

    const devices = new Set<string>();
    streamsToConfigure.forEach((e) => devices.add(Utils.RegEx.findIPAddress(e.rtspUrl)));
    const map = new Map<string, VideoStream[]>();
    Array.from(devices).forEach((name) => {
      map.set(name, streamsToConfigure.filter(stream => Utils.RegEx.findIPAddress(stream.rtspUrl) === name));
    })

    let currentIndex = 0;

    console.log(map);

    map.forEach((streams) => {
      const EDGE_DEVICE = getEdgeDeviceToSendMessage(streams);

      const uuid: string = v4();
      const sub = API.PubSub.subscribeToTopic(`edge/${EDGE_DEVICE}/http/responses/${uuid}`, (message: any) => {
        console.log("Incoming message: ", message);
        subsRef.current.push(sub);
        sub.unsubscribe();
        if (message) {
          if (message.status) {
            if (message.status === 200) {
              setSuccess((currentValue) => [...currentValue, ...streams.map(stream => ({ kvsName: stream.kvsName, name: `${stream.defaultName} (${stream.externalId})` }))])
              setPending((p) => p.filter(el => streams.find(stream => stream.kvsName === el.kvsName) === undefined))
            } else {
             setFailed((currentValue) => [...currentValue, ...streams.map(stream => ({ kvsName: stream.kvsName, name: `${stream.defaultName} (${stream.externalId})` }))])
              setPending((p) => p.filter(el => streams.find(stream => stream.kvsName === el.kvsName) === undefined))
            }
          }
        }
      });

      const message = {
        "device": {
          "manufacturer": first(streams)?.manufacturer || "",
          "type": first(streams)?.type || "",
          "url": Utils.RegEx.findIPAddress(first(streams)?.rtspUrl || "")
        },
        "action": "autofocus/configure",
        "parameters": {
          "deviceStreams": streams.map(stream => (
            {
              "deviceId": +getCameraNumber(stream),
              "kvsName": stream.kvsName,
              "cameraModel": stream?.cameraModel || ""
            }
          ))
        }
      };

      console.log('Sending configuration to topic: ', `edge/${EDGE_DEVICE}/http/requests/${uuid}`);
      console.log('Message: ', message)
      setTimeout(() => {
        API.PubSub.publishOnTopic(`edge/${EDGE_DEVICE}/http/requests/${uuid}`, message)
      }, 1000 * currentIndex);
      currentIndex += 1;
    })
  }, [streams])

  const startGeofenceRoutine = React.useCallback(() => {
    setStartedConfiguration(true);

    const skipped: Request[] = [];
    const pending: Request[] = [];

    streams.forEach((stream) => {
      if (stream.externalId) {
        pending.push({ name: `${stream.defaultName} (${stream.externalId})`, kvsName: stream.kvsName });
      } else {
        skipped.push({ name: `${stream.defaultName} (${stream.externalId})`, kvsName: stream.kvsName });
      }
    })

    setSkipped(skipped);
    setPending(pending);

    const streamsToConfigure = streams.filter(el => pending.find(e => e.kvsName === el.kvsName));

    const devices = new Set<string>();
    streamsToConfigure.forEach((e) => devices.add(e.externalId?.split('-')[0] || ""));
    const map = new Map<string, VideoStream[]>();
    Array.from(devices).forEach((name) => {
      map.set(name, streamsToConfigure.filter(stream => stream.externalId?.includes(name)));
    })

    const onSuccess = (streams: VideoStream[]) => {
      setSuccess((currentValue) => [...currentValue, ...streams.map(stream => ({ kvsName: stream.kvsName, name: `${stream.defaultName} (${stream.externalId})` }))])
      setPending((p) => p.filter(el => streams.find(stream => stream.kvsName === el.kvsName) === undefined))
    }

    const onError = (streams: VideoStream[]) => {
      setFailed((currentValue) => [...currentValue, ...streams.map(stream => ({ kvsName: stream.kvsName, name: `${stream.defaultName} (${stream.externalId})` }))])
      setPending((p) => p.filter(el => streams.find(stream => stream.kvsName === el.kvsName) === undefined))
    }

    const limit = pLimit(10);
    const input: any[] = [];

    map.forEach((streams) => {
      input.push(limit(() => performConfigureGeofenceRequest(streams, onSuccess, onError, onError)))
    })

    Promise.all(input);
  }, [streams])

  React.useEffect(() => {
    if (dialogOpen === false) {
      setPending([]);
      setSkipped([]);
      setSuccess([]);
      setFailed([]);
      setStartedConfiguration(false);
      subsRef.current.forEach((sub) => {
        try {
          sub.unsubscribe();
        } catch (error) { }
      })
    }
  }, [dialogOpen])

  return (
    <>
      {staticMode !== true &&
        <Box position="absolute" top="calc(100vh - 4rem)" left="calc(100vw - 4rem)">
          <Tooltip title="Custom configurations">
            <Fab
              onClick={openDialog}
              data-cy="views-configure-geofences-button"
              size="small"
              color="primary"
            >
              <Settings />
            </Fab>
          </Tooltip>
        </Box>
      }
      <Dialog transitionDuration={0} open={Boolean(dialogOpen || staticMode)} onClose={onClose}>
        <Box
          padding="1.5rem"
          width="80vw"
          height="80vh"
          display="grid"
          gridTemplateRows="auto max-content"
        >
          {startedConfiguration === false &&
            <Box
              width="100%"
              height="100%"
              display="grid"
              gridTemplateRows="max-content max-content auto"
              gap='1rem'
            >
              <Typography variant="h5">Streams feature configuration</Typography>
              <Typography variant="subtitle1" >Please choose the features you would like to configure</Typography>
              <Box display="grid" width="10rem" gridTemplateRows="max-content max-content max-content" gap="1rem">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={startGeofenceRoutine}
                >
                  Axis Geofences
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={startAutopilotRoutine}
                >
                  Autopilot
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={startAutofocusRoutine}
                >
                  Autofocus
                </Button>
              </Box>
            </Box>
          }
          {startedConfiguration === true &&
            <Box display="grid" width="100%" height="100%" padding="1.5rem" gridTemplateColumns="1fr 1fr 1fr 1fr" gap="1rem">
              <Box maxHeight="66vh" overflow="auto" width="100%" height="100%" border="1px solid rgba(0, 0, 0, 0.4)">
                <Box width='100%' display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                  <Typography variant="h6" >Pending</Typography>
                </Box>
                {pending.map((request: Request) => (
                  <Box key={request.kvsName} display="grid" gridTemplateColumns="max-content max-content" gap="0.5rem" padding="0.5rem" alignItems="center">
                    <CircularProgress size="1rem" />
                    <Typography>{request.name || request.kvsName}</Typography>
                  </Box>
                ))}
              </Box>
              <Box maxHeight="66vh" overflow="auto" width="100%" height="100%" border="1px solid rgba(0, 0, 0, 0.4)">
                <Box width='100%' display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                  <Typography variant="h6" >Succeeded</Typography>
                </Box>
                {success.map((request: Request) => (
                  <Box key={request.kvsName} display="grid" gridTemplateColumns="max-content max-content" gap="0.5rem" padding="0.5rem" alignItems="center">
                    <Check color='success' />
                    <Typography>{request.name || request.kvsName}</Typography>
                  </Box>
                ))}
              </Box>
              <Box maxHeight="66vh" overflow="auto" width="100%" height="100%" border="1px solid rgba(0, 0, 0, 0.4)">
                <Box width='100%' display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                  <Typography variant="h6" >Failed</Typography>
                </Box>
                {failed.map((request: Request) => (
                  <Box key={request.kvsName} display="grid" gridTemplateColumns="max-content max-content" gap="0.5rem" padding="0.5rem" alignItems="center">
                    <Error color="error" />
                    <Typography>{request.name || request.kvsName}</Typography>
                  </Box>
                ))}
              </Box>
              <Box maxHeight="66vh" overflow="auto" width="100%" height="100%" border="1px solid rgba(0, 0, 0, 0.4)">
                <Box width='100%' display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                  <Typography variant="h6" >Skipped</Typography>
                </Box>
                {skipped.map((request: Request) => (
                  <Box key={request.kvsName} display="grid" gridTemplateColumns="max-content max-content" gap="0.5rem" padding="0.5rem" alignItems="center">
                    <Check />
                    <Typography>{request.name || request.kvsName}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          }
          <Box width="100%" display="flex" flexDirection="row" alignItems="flex-end" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              onClick={closeDialog}
              disableElevation
            >
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

export default CustomStreamsConfiguration;
